// Pinia store for the Triage page: the stek-level queue of issues awaiting a
// human decision, the RESOLVED decision records (revisitable, undoable — the
// reconcile pattern), and the Gotoko guidance. Open issues are DERIVED
// views over the external communication map's unresolved list; resolved
// issues project the decision store. Both share the same per-pool issue id,
// so resolving an issue keeps the selection: the rail row flips to the
// Resolved group and the pane shows the decision result — no jump, no
// dead-end. Advancing is explicit ("Next unresolved").
import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";

import {
    buildEntityIssueIndex,
    buildIssueBpmnIndex,
    buildRollupCounts,
    issueInScope,
    subtreeIds,
} from "@/components/Triage/triage-entity-index";
import {
    type ModelIssue,
    type ResolvedPoolIssue,
    type TriageIssue,
    modelIssue,
    resolvedPoolIssue,
    unresolvedPoolIssue,
    unresolvedPoolIssueId,
} from "@/components/Triage/triage-issues";
import { useEditManager } from "@/composables/edit-manager";
import { useWorktreeStore } from "@/composables/worktree-manager";
import { api, type types } from "@/lib/openapi";
import { PartyType } from "@/lib/openapi/gen";
import { useExternalCommunicationStore } from "@/stores/external-communication-store";
import { useReleaseStore } from "@/stores/release-store";
import { useUserManager } from "@/stores/user-store";

export type {
    ModelIssue,
    TriageIssue,
    TriageIssueType,
} from "@/components/Triage/triage-issues";
export {
    CATEGORY_META,
    TRIAGE_TYPE_META,
} from "@/components/Triage/triage-issues";

export type PoolTriageSuggestion = types.PoolTriageSuggestion;

/** A suggestion the mass-approve can apply: marked safe AND its answer has
 *  an unattended write path — party creation, or role promotion (which
 *  derives its domains itself). Applications/references need a pick. */
export const isApprovableSuggestion = (
    suggestion: PoolTriageSuggestion
): boolean =>
    suggestion.safe &&
    (suggestion.recommend === "party" ||
        suggestion.recommend === "internal_function");

export const useTriageStore = defineStore("triage", () => {
    const toast = useToast();
    const externalCommunicationStore = useExternalCommunicationStore();
    const { unresolved, activeDecisions, isLoading } = storeToRefs(
        externalCommunicationStore
    );
    // Belt-and-braces mirror of the server-side per-rule gate (D-0
    // amendment): the backend already withholds disabled rules from
    // get_triage_issues and the pool pre-sort, but the pool QUEUE tier
    // derives client-side from the communication map, so its gate lives here.
    const { enabledTriageRules } = useUserManager();
    const releaseStore = useReleaseStore();
    const { isViewOnly } = useEditManager();

    const issues = computed<TriageIssue[]>(() =>
        enabledTriageRules.value.has("unresolved-external-pool")
            ? unresolved.value.map(unresolvedPoolIssue)
            : []
    );

    // The derived model issues (BPMN-alignment lint + consistency findings),
    // straight from the backend — open ones queue, dismissed ones stay
    // selectable (dimmed) so their dismissal can be reopened.
    const modelIssuesRaw = ref<types.TriageIssue[]>([]);
    const modelIssuesLoaded = ref(false);

    const fetchModelIssues = async () => {
        try {
            const { data, response } =
                await api.startDevTriageMgtGetTriageIssuesStartPost({});
            if (response.ok && data) {
                modelIssuesRaw.value = data.issues ?? [];
            }
        } catch {
            // The queue works without the model-issue tier.
        } finally {
            modelIssuesLoaded.value = true;
        }
    };

    // Server-filtered already; the client filter keeps a stale /user/me and a
    // fresh issue list (or vice versa) from disagreeing about a rule flip.
    const enabledModelIssues = computed(() =>
        modelIssuesRaw.value.filter((issue) =>
            enabledTriageRules.value.has(issue.issue_type)
        )
    );

    const openModelIssues = computed<ModelIssue[]>(() =>
        enabledModelIssues.value
            .filter((issue) => issue.status === "open")
            .map(modelIssue)
    );
    const dismissedModelIssues = computed<ModelIssue[]>(() =>
        enabledModelIssues.value
            .filter((issue) => issue.status === "dismissed")
            .map(modelIssue)
    );

    // The Resolved group: one row per ACTIVE decision, newest first. Gated on
    // the pool rule like the queue above — a disabled rule renders NOWHERE, and
    // an ungated Resolved group would keep offering panes for a rule whose open
    // issues, counts and indicators are all dark.
    const resolvedIssues = computed<ResolvedPoolIssue[]>(() =>
        enabledTriageRules.value.has("unresolved-external-pool")
            ? [...activeDecisions.value]
                  .sort((a, b) =>
                      (b.decided_at ?? "").localeCompare(a.decided_at ?? "")
                  )
                  .map(resolvedPoolIssue)
            : []
    );

    // Gotoko guidance, keyed by issue id. Arrives AFTER the queue (its
    // request runs an LLM pass) — the UI must read "no suggestion yet" as
    // pending until `suggestionsLoaded` flips.
    const suggestions = ref<Record<string, PoolTriageSuggestion>>({});
    const suggestionsLoaded = ref(false);

    const fetchSuggestions = async () => {
        // Deriving suggestions spends a model call and commits the cache into
        // the stek repo, which a viewer may not do (the backend refuses with
        // a 403). They triage without guidance.
        if (isViewOnly.value) {
            suggestionsLoaded.value = true;
            return;
        }
        try {
            const { data, response } =
                await api.startDevApplicationMgtGetPoolTriageSuggestionsStartPost(
                    {}
                );
            if (response.ok && data) {
                suggestions.value = Object.fromEntries(
                    data.map((suggestion) => [
                        unresolvedPoolIssueId(suggestion.pool_name),
                        suggestion,
                    ])
                );
            }
        } catch {
            // Guidance is best-effort — the queue works without it.
        } finally {
            suggestionsLoaded.value = true;
        }
    };

    const suggestionFor = (issueId: string): PoolTriageSuggestion | undefined =>
        suggestions.value[issueId];

    // --- entity index & scope (the Improvements layer's data spine) ---
    //
    // Everything that surfaces issues per entity — sidebar tree badges,
    // ancestor roll-ups, diagram dots, the `?entity=` scoped queue — derives
    // from these computeds over the RAW (unscoped) issue sources. Single
    // choke point by design: future issue tiers filter here, once.
    const worktreeStore = useWorktreeStore();

    /** Every open issue, pool and model tiers together (unscoped). */
    const allOpenIssues = computed<TriageIssue[]>(() => [
        ...issues.value,
        ...openModelIssues.value,
    ]);

    /** Entity identity → its own open issues (direct badges, indicators). */
    const entityIssueMap = computed(() =>
        buildEntityIssueIndex(allOpenIssues.value)
    );

    /** Domain/product identity → distinct open issues in its subtree. */
    const entityRollupCounts = computed(() =>
        buildRollupCounts(worktreeStore.worktree, entityIssueMap.value)
    );

    /** Process identity → BPMN element ids carrying an open model issue. */
    const issueBpmnIdsByProcess = computed(() =>
        buildIssueBpmnIndex(openModelIssues.value)
    );

    const issueCountForEntity = (entityId: string): number =>
        entityIssueMap.value.get(entityId)?.length ?? 0;

    const rollupCountForEntity = (entityId: string): number =>
        entityRollupCounts.value.get(entityId) ?? 0;

    /** Titles of open model issues evidenced at a BPMN element (layer-dot
     *  tooltip). Matched on the element id alone — ids are diagram-unique. */
    const issueTitlesForBpmnElement = (bpmnId: string): string[] =>
        openModelIssues.value
            .filter(
                (issue) =>
                    (issue.apiIssue.occurrences ?? []).some(
                        (occurrence) => occurrence.bpmn_id === bpmnId
                    ) ||
                    (issue.apiIssue.lanes ?? []).some(
                        (lane) => lane.lane_bpmn_id === bpmnId
                    )
            )
            .map((issue) => issue.apiIssue.title);

    // The active scope (`?entity=` on the triage route): the queue narrows to
    // the issues evidenced in that entity's subtree. Null = the full stek.
    const scopeEntityId = ref<string | null>(null);
    const setScope = (entityId: string | null) => {
        scopeEntityId.value = entityId;
    };
    const scopeEntityIds = computed<Set<string> | null>(() =>
        scopeEntityId.value
            ? subtreeIds(worktreeStore.worktree, scopeEntityId.value)
            : null
    );
    const inScope = (issue: TriageIssue): boolean =>
        !scopeEntityIds.value || issueInScope(issue, scopeEntityIds.value);

    /** The scope's pool-tier issues (all of them when no scope is active). */
    const scopedPoolIssues = computed(() => issues.value.filter(inScope));
    /** The scope's open model issues — the queue's category groups. */
    const scopedModelIssues = computed(() =>
        openModelIssues.value.filter(inScope)
    );
    const scopedResolvedIssues = computed(() =>
        resolvedIssues.value.filter(inScope)
    );
    const scopedDismissedModelIssues = computed(() =>
        dismissedModelIssues.value.filter(inScope)
    );

    // Two-tier split, the reconcile queue's shape: issues whose suggestion is
    // safe AND applicable collect in the Gotoko tier (the mass-approve
    // candidates); everything else needs the user's judgment. Scope-aware:
    // on a scoped queue the mass-approve only touches the scope's pools.
    const safeIssues = computed(() =>
        scopedPoolIssues.value.filter((issue) => {
            const suggestion = suggestions.value[issue.id];
            return suggestion && isApprovableSuggestion(suggestion);
        })
    );
    const attentionIssues = computed(() =>
        scopedPoolIssues.value.filter(
            (issue) => !safeIssues.value.some((safe) => safe.id === issue.id)
        )
    );
    /** The rail's top-to-bottom OPEN order: attention pools, safe pools,
     *  then the model-issue categories (already category-sorted by the
     *  backend). The Resolved/Dismissed groups render below. Scope-aware. */
    const orderedIssues = computed(() => [
        ...attentionIssues.value,
        ...safeIssues.value,
        ...scopedModelIssues.value,
    ]);

    /** Everything the top banner / topbar counts as "needs a decision",
     *  within the active scope. */
    const openCount = computed(
        () => scopedPoolIssues.value.length + scopedModelIssues.value.length
    );

    /** The stek-wide open count, scope-independent — the "Improve your
     *  stek" card and the scoped queue's more-across-your-stek CTA. */
    const totalOpenCount = computed(() => allOpenIssues.value.length);

    const selectedId = ref<string | null>(null);

    // Selection resolves against open AND resolved/dismissed issues: the ids
    // are shared per pool, so a just-resolved pool keeps its selection and
    // the pane flips to the decision result in place; a just-dismissed model
    // issue likewise keeps its pane (now showing Reopen).
    const selected = computed<TriageIssue | null>(
        () =>
            issues.value.find((issue) => issue.id === selectedId.value) ??
            resolvedIssues.value.find(
                (issue) => issue.id === selectedId.value
            ) ??
            openModelIssues.value.find(
                (issue) => issue.id === selectedId.value
            ) ??
            dismissedModelIssues.value.find(
                (issue) => issue.id === selectedId.value
            ) ??
            null
    );

    const select = (id: string | null) => {
        selectedId.value = id;
    };

    /** Explicit advance — the "Next unresolved" button / Enter key. Picks
     *  the first open issue after the current rail position, else the first
     *  open issue. */
    const nextUnresolved = () => {
        const open = orderedIssues.value;
        if (!open.length) return;
        const currentIndex = open.findIndex(
            (issue) => issue.id === selectedId.value
        );
        const next = open[currentIndex + 1] ?? open[0];
        selectedId.value = next.id;
    };

    // Only when the selected id vanishes ENTIRELY (pool gone from the
    // models, decision retracted elsewhere) or falls outside a newly applied
    // scope does the selection fall back — never on a resolve, which keeps
    // its id via the resolved issue.
    watch(
        [
            issues,
            resolvedIssues,
            openModelIssues,
            dismissedModelIssues,
            scopeEntityIds,
        ],
        () => {
            if (selectedId.value === null) return;
            if (selected.value !== null && inScope(selected.value)) return;
            selectedId.value = orderedIssues.value[0]?.id ?? null;
        }
    );

    /** (Re)load every issue source; per-type loaders fan out from here.
     *  Single-flight: concurrent callers (the layout's badge loader and the
     *  Triage page both watch the stek identity) share one request burst. */
    let loadInFlight: Promise<void> | null = null;
    const load = (): Promise<void> => {
        if (loadInFlight) return loadInFlight;
        const revision = releaseStore.activeRevision;
        loadInFlight = (async () => {
            void fetchSuggestions();
            void fetchModelIssues();
            // Role-link candidates arrive best-effort — the internal-function
            // panel degrades to create-only without them.
            void externalCommunicationStore.fetchStekRoles();
            await Promise.all([
                externalCommunicationStore.fetchMap(),
                externalCommunicationStore.fetchDecisions(),
            ]);
        })().finally(() => {
            loadInFlight = null;
            // The revision moved while this burst flew (the user entered or
            // left a thread mid-fetch): the results describe the old
            // revision — chase them with a burst at the current one.
            if (releaseStore.activeRevision !== revision) void load();
        });
        return loadInFlight;
    };

    // Entering/leaving a thread branch changes the revision every issue
    // source derives at (the ambient x-thread-revision header) — refetch on
    // the flip so the queue and the per-entity improvement badges show the
    // branch's issues inside a thread and the working copy's outside one.
    // Gated on a prior load: a cold store waits for its page/layout to
    // trigger the first fetch.
    watch(
        () => releaseStore.activeRevision,
        () => {
            if (!modelIssuesLoaded.value) return;
            void load();
        }
    );

    /**
     * Mass-approve Gotoko's safe suggestions: nothing was applied
     * until this click. Each approval applies the suggested answer through
     * the resolution verbs — a typed party of the suggested type, or a role
     * promotion — writing a decision record per pool; the rows move to the
     * Resolved group (undo stays available per pool).
     */
    const approvingSafe = ref(false);
    const approveSafe = async () => {
        if (approvingSafe.value || !safeIssues.value.length) return;
        approvingSafe.value = true;
        try {
            let applied = 0;
            let failed = 0;
            for (const issue of [...safeIssues.value]) {
                if (issue.type !== "unresolved-external-pool") continue;
                const suggestion = suggestions.value[issue.id];
                const result =
                    suggestion.recommend === "internal_function"
                        ? await externalCommunicationStore.resolvePoolAsRole(
                              issue.poolName
                          )
                        : await externalCommunicationStore.resolvePoolAsParty({
                              poolName: issue.poolName,
                              partyType:
                                  (suggestion.party_type as PartyType) ??
                                  PartyType.OTHER,
                          });
                if (result) applied += 1;
                else failed += 1;
            }
            if (failed) {
                toast.add({
                    severity: "error",
                    summary: "Not every suggestion could be applied",
                    detail: `${failed} of ${applied + failed} failed — they stay in the queue.`,
                    life: 5000,
                });
            } else if (applied) {
                toast.add({
                    severity: "success",
                    summary: "Suggestions applied",
                    detail: `${applied} pool${applied === 1 ? "" : "s"} resolved as suggested.`,
                    life: 4000,
                });
            }
        } finally {
            approvingSafe.value = false;
        }
    };

    /**
     * Dismiss a model issue ("seen it, leaving it"): records a dismissal
     * pinned to the issue's evidence fingerprint. When the diagrams change,
     * the dismissal goes stale and the issue resurfaces on its own.
     */
    const dismissIssue = async (
        issue: ModelIssue,
        reason?: string
    ): Promise<boolean> => {
        try {
            const { response } =
                await api.startDevTriageMgtDismissTriageIssueStartPost({
                    body: {
                        issue_key: issue.apiIssue.key,
                        fingerprint: issue.apiIssue.fingerprint,
                        reason: reason?.trim() || null,
                    },
                });
            if (!response.ok) throw new Error();
            await fetchModelIssues();
            return true;
        } catch {
            toast.add({
                severity: "error",
                summary: "Could not dismiss the issue",
                detail: "Reload and try again.",
                life: 5000,
            });
            return false;
        }
    };

    /** Reopen a dismissed model issue (retract the dismissal). */
    const reopenIssue = async (issue: ModelIssue): Promise<boolean> => {
        try {
            const { response } =
                await api.startDevTriageMgtReopenTriageIssueStartPost({
                    body: { issue_key: issue.apiIssue.key },
                });
            if (!response.ok) throw new Error();
            await fetchModelIssues();
            return true;
        } catch {
            toast.add({
                severity: "error",
                summary: "Could not reopen the issue",
                detail: "Reload and try again.",
                life: 5000,
            });
            return false;
        }
    };

    /**
     * Fix verb for "system-lane-on-fake-role": stamp the direct
     * lane→application link on the given lanes (dropping the stand-in role
     * link unless kept). The issue disappears from the derived evidence.
     */
    const linkLanesToApplication = async (options: {
        applicationId: string;
        lanes: Array<{ processId: string; laneBpmnId: string }>;
        clearRole: boolean;
    }): Promise<boolean> => {
        try {
            const { response } =
                await api.startDevTriageMgtLinkLanesToApplicationStartPost({
                    body: {
                        application_id: options.applicationId,
                        lanes: options.lanes.map((lane) => ({
                            process_id: lane.processId,
                            lane_bpmn_id: lane.laneBpmnId,
                        })),
                        clear_role: options.clearRole,
                    },
                });
            if (!response.ok) throw new Error();
            toast.add({
                severity: "success",
                summary: "Lanes linked",
                detail: "The lanes now bind their tasks to the application.",
                life: 4000,
            });
            await fetchModelIssues();
            return true;
        } catch {
            toast.add({
                severity: "error",
                summary: "Could not link the lanes",
                detail: "Reload and try again.",
                life: 5000,
            });
            return false;
        }
    };

    /**
     * The one accept of "named-recipient-without-party": apply the previewed
     * black-box pool (fingerprint-pinned to `proposeRestructure`) in every
     * process that names the recipient AND resolve the new pool as an
     * external party — linked via `partyId`, or created from `name` /
     * `partyType` / `description` — in one transaction. Returns the pool's
     * decision record. A drift conflict recomputes the preview so the accept
     * re-arms on what is actually there.
     */
    const addExternalPool = async (options: {
        issueKey: string;
        proposalFingerprint: string;
        partyId?: string | null;
        name?: string | null;
        partyType?: PartyType | null;
        description?: string | null;
    }): Promise<types.PoolTriageDecision | null> => {
        try {
            const { data, response } =
                await api.startDevTriageMgtAddExternalPoolStartPost({
                    body: {
                        issue_key: options.issueKey,
                        proposal_fingerprint: options.proposalFingerprint,
                        party_id: options.partyId ?? null,
                        name: options.name ?? null,
                        party_type: options.partyType ?? null,
                        description: options.description?.trim() || null,
                    },
                });
            if (response.status === 409) {
                toast.add({
                    severity: "warn",
                    summary: "The diagrams changed since the preview",
                    detail: "The preview was recomputed — review and accept again.",
                    life: 6000,
                });
                proposal.value = null;
                void proposeRestructure(options.issueKey);
                return null;
            }
            if (!response.ok || !data) throw new Error();
            proposal.value = null;
            toast.add({
                severity: "success",
                summary: "External pool added",
                detail:
                    `«${data.pool_name}» is now a pool in the diagrams, ` +
                    `resolved as the external party ${data.target?.name ?? ""}.`,
                life: 5000,
            });
            await Promise.all([
                fetchModelIssues(),
                externalCommunicationStore.fetchMap(),
                externalCommunicationStore.fetchDecisions(),
            ]);
            return data;
        } catch {
            toast.add({
                severity: "error",
                summary: "Could not add the pool",
                detail: "Reload and try again.",
                life: 5000,
            });
            return null;
        }
    };

    /**
     * The non-party answers of "named-recipient-without-party": record the
     * recipient as a system (linking the naming tasks to it), an internal
     * function (a role) or an internal reference — no diagram edit; the
     * record retires the issue and lands under Resolved.
     */
    const resolveNamedRecipient = async (options: {
        issueKey: string;
        resolution: "application" | "internal_function" | "internal_reference";
        applicationId?: string | null;
        roleId?: string | null;
        entityKind?: string | null;
        entityId?: string | null;
    }): Promise<types.PoolTriageDecision | null> => {
        try {
            const { data, response } =
                await api.startDevTriageMgtResolveNamedRecipientStartPost({
                    body: {
                        issue_key: options.issueKey,
                        resolution: options.resolution,
                        application_id: options.applicationId ?? null,
                        role_id: options.roleId ?? null,
                        entity_kind: options.entityKind ?? null,
                        entity_id: options.entityId ?? null,
                    },
                });
            if (!response.ok || !data) throw new Error();
            toast.add({
                severity: "success",
                summary: "Recipient resolved",
                detail: `«${data.pool_name}» is recorded as ${data.target?.name ?? data.resolution}.`,
                life: 5000,
            });
            await Promise.all([
                fetchModelIssues(),
                externalCommunicationStore.fetchMap(),
                externalCommunicationStore.fetchDecisions(),
            ]);
            return data;
        } catch {
            toast.add({
                severity: "error",
                summary: "Could not resolve the recipient",
                detail: "Reload and try again.",
                life: 5000,
            });
            return null;
        }
    };

    /**
     * Fix verb for "lane-alias-cluster": rename the merged spellings to the
     * survivor across the stek's definitions (a diagram edit — explicit
     * opt-in, never auto-applied), optionally linking one existing role.
     */
    const unifyLaneAliases = async (options: {
        survivorName: string;
        mergeNames: string[];
        roleId?: string | null;
    }): Promise<boolean> => {
        try {
            const { response } = await api.startDevTriageMgtUnifyLanesStartPost(
                {
                    body: {
                        survivor_name: options.survivorName,
                        merge_names: options.mergeNames,
                        role_id: options.roleId ?? null,
                    },
                }
            );
            if (!response.ok) throw new Error();
            toast.add({
                severity: "success",
                summary: "Lanes unified",
                detail: `The spellings were renamed to «${options.survivorName}» in the diagrams.`,
                life: 4000,
            });
            await fetchModelIssues();
            return true;
        } catch {
            toast.add({
                severity: "error",
                summary: "Could not unify the lanes",
                detail: "Reload and try again.",
                life: 5000,
            });
            return false;
        }
    };

    // --- stacked resolutions: restructure preview + apply ---

    /** The loaded restructure proposal, keyed to the issue it previews. */
    const proposal = ref<types.RestructureProposal | null>(null);
    const proposalLoading = ref(false);

    /**
     * Load the server-computed restructure preview for a stacked-resolution
     * issue: per-process after-XML + change summary + removed elements, and
     * the fingerprint the apply verbs require.
     */
    const proposeRestructure = async (issueKey: string): Promise<boolean> => {
        proposalLoading.value = true;
        try {
            const { data, response } =
                await api.startDevTriageMgtProposeRestructureStartPost({
                    body: { issue_key: issueKey },
                });
            if (!response.ok || !data) throw new Error();
            proposal.value = data;
            return true;
        } catch {
            proposal.value = null;
            toast.add({
                severity: "error",
                summary: "Could not compute the restructure preview",
                detail: "Reload and try again.",
                life: 5000,
            });
            return false;
        } finally {
            proposalLoading.value = false;
        }
    };

    const clearProposal = () => {
        proposal.value = null;
    };

    /** The decision-time BPMN preview of a pool resolution (rename + the
     *  eventual lane end-state), loaded before the user commits. The pane
     *  eager-loads it on every configuration change, so responses race:
     *  latest-wins — a superseded (or cleared-over) request never lands. */
    const poolPreview = ref<types.PoolResolutionPreview | null>(null);
    const poolPreviewLoading = ref(false);
    let poolPreviewSeq = 0;

    const proposePoolResolution = async (options: {
        poolName: string;
        renameFrom?: string[];
        roleId?: string | null;
        applicationId?: string | null;
    }): Promise<boolean> => {
        const seq = ++poolPreviewSeq;
        poolPreviewLoading.value = true;
        try {
            const { data, response } =
                await api.startDevApplicationMgtProposePoolResolutionStartPost({
                    body: {
                        pool_name: options.poolName,
                        rename_from: options.renameFrom ?? null,
                        role_id: options.roleId ?? null,
                        application_id: options.applicationId ?? null,
                    },
                });
            if (seq !== poolPreviewSeq) return false;
            if (!response.ok || !data) throw new Error();
            poolPreview.value = data;
            return true;
        } catch {
            if (seq !== poolPreviewSeq) return false;
            poolPreview.value = null;
            toast.add({
                severity: "error",
                summary: "Could not compute the BPMN preview",
                detail: "Reload and try again.",
                life: 5000,
            });
            return false;
        } finally {
            if (seq === poolPreviewSeq) poolPreviewLoading.value = false;
        }
    };

    const clearPoolPreview = () => {
        // Invalidate any in-flight request too — a response landing after
        // the clear must not resurrect a stale (mis-fingerprinted) preview.
        poolPreviewSeq += 1;
        poolPreview.value = null;
        poolPreviewLoading.value = false;
    };

    /** Shared refresh + toast after a successful restructure apply. */
    const afterRestructure = async (detail: string) => {
        proposal.value = null;
        toast.add({
            severity: "success",
            summary: "Diagram restructured",
            detail,
            life: 4000,
        });
        await Promise.all([
            fetchModelIssues(),
            externalCommunicationStore.fetchMap(),
            externalCommunicationStore.fetchDecisions(),
        ]);
    };

    const restructureError = () =>
        toast.add({
            severity: "error",
            summary: "Could not apply the restructure",
            detail: "The diagrams may have changed since the preview — reload.",
            life: 5000,
        });

    /**
     * Apply the pool→lane restructure (internal-actor-as-pool /
     * internal-system-as-pool step 2). Requires the previewed fingerprint.
     */
    const convertPoolToLane = async (options: {
        poolName: string;
        proposalFingerprint: string;
    }): Promise<boolean> => {
        try {
            const { response } =
                await api.startDevTriageMgtConvertPoolToLaneStartPost({
                    body: {
                        pool_name: options.poolName,
                        proposal_fingerprint: options.proposalFingerprint,
                    },
                });
            if (!response.ok) throw new Error();
            await afterRestructure(
                `«${options.poolName}» is now a lane of the organisation's own pool.`
            );
            return true;
        } catch {
            restructureError();
            return false;
        }
    };

    /**
     * Apply the lane→pool restructure (external-party-as-lane). The
     * destructive edit — the preview enumerated the removed tasks.
     */
    const convertLaneToPool = async (options: {
        lanes: Array<{ processId: string; laneBpmnId: string }>;
        proposalFingerprint: string;
    }): Promise<boolean> => {
        try {
            const { response } =
                await api.startDevTriageMgtConvertLaneToPoolStartPost({
                    body: {
                        lanes: options.lanes.map((lane) => ({
                            process_id: lane.processId,
                            lane_bpmn_id: lane.laneBpmnId,
                        })),
                        proposal_fingerprint: options.proposalFingerprint,
                    },
                });
            if (!response.ok) throw new Error();
            await afterRestructure(
                "The lanes are now black-box pools with message flows."
            );
            return true;
        } catch {
            restructureError();
            return false;
        }
    };

    return {
        issues,
        resolvedIssues,
        attentionIssues,
        safeIssues,
        orderedIssues,
        openCount,
        totalOpenCount,
        openModelIssues,
        dismissedModelIssues,
        entityIssueMap,
        entityRollupCounts,
        issueBpmnIdsByProcess,
        issueCountForEntity,
        rollupCountForEntity,
        issueTitlesForBpmnElement,
        scopeEntityId,
        scopeEntityIds,
        setScope,
        scopedModelIssues,
        scopedResolvedIssues,
        scopedDismissedModelIssues,
        modelIssuesLoaded,
        suggestions,
        suggestionsLoaded,
        selectedId,
        selected,
        isLoading,
        approvingSafe,
        load,
        fetchSuggestions,
        fetchModelIssues,
        suggestionFor,
        select,
        nextUnresolved,
        approveSafe,
        dismissIssue,
        reopenIssue,
        addExternalPool,
        resolveNamedRecipient,
        linkLanesToApplication,
        unifyLaneAliases,
        proposal,
        proposalLoading,
        proposeRestructure,
        clearProposal,
        poolPreview,
        poolPreviewLoading,
        proposePoolResolution,
        clearPoolPreview,
        convertPoolToLane,
        convertLaneToPool,
    };
});
